import React, { useEffect } from 'react'
import classnames from 'classnames/bind'
import InfiniteScroll from 'react-infinite-scroller'
import { useRouter } from 'next/router'
import { isNull } from 'lodash-es'
import { useTranslation } from 'next-i18next'

// Libs
import useMedia from '@/hooks/useMedia'
import { handleGtmViewItemList } from '@/methods/handleGtmEvent'
import { useObserver } from '@/hooks/useObserver'
import handleRoutePushWithQuery from '@/methods/url/handleRoutePushWithQuery'

// Components
import ProductCard from '@/components/shared/ProductCard'
import Empty from '../Empty'
import HotLiveList from '@/components/shared/HotLiveList'
import ProductCardSkeleton from '@/components/shared/ProductCard/skeleton/ProductCardSkeleton'
import VirtualizedMallList from './components/VirtualizedMallList'
import SortItem from './components/SortItem'

// Constants
import { MALL_DEFAULT_PAGE_SIZE } from '@/constants/mall'
import { ProductInfoType } from '@/api/fetchProductData'
import { ROUTE_PATH } from '@/constants/routePath'
import { MALL_SORT, ORDER, DEFAULT_MALL_SORT, DEFAULT_MALL_ORDER } from '@/constants/mall'

// Styles
import styles from './style.module.scss'

const cx = classnames.bind(styles)

type TProductCardDataAttributes = {
  dataset: {
    id: string
  }
} & Element

type MallListProps = {
  className?: string
  categoryListData: any
  categoryId?: string
  gtmItemEventParams: {
    itemListName: string
    itemListId?: string
  }
}

const MallList = (props: MallListProps) => {
  const { className, categoryListData, gtmItemEventParams } = props
  const { isMobile } = useMedia()
  const { t } = useTranslation()
  const router = useRouter()
  const { query, pathname } = router

  const { sortBy = '', order = '', category = '', category_id = '' } = query

  const isMallPage = router.pathname === ROUTE_PATH.MALL
  const isVirtualized = isMobile && isMallPage
  const productList = categoryListData?.data?.pages
    .map((item: { productsInfo: ProductInfoType[] }) => item?.productsInfo)
    .flat()
    .filter((rowItem: { productsInfo: ProductInfoType[] }) => !isNull(rowItem))

  const isIntersecCb = (entries: IntersectionObserverEntry[]) => {
    const isIntersectingEntries = entries.filter((entry: IntersectionObserverEntry) => entry.isIntersecting)
    const dataAttributes = isIntersectingEntries.map((entry) => (entry.target as TProductCardDataAttributes).dataset)
    const productIdList = dataAttributes.map((item) => Number(item.id))
    if (productIdList.length > 0 && productList) {
      const intersectingProductList = productList.filter((productItem: ProductInfoType) => productIdList.includes(productItem?.productId))
      handleGtmViewItemList({ ...gtmItemEventParams, productInfoList: intersectingProductList })
    }
  }

  const { observerRef } = useObserver({ observerCallback: isIntersecCb, dep: [categoryListData.data] })
  const handleNextpage = () => {
    if (!categoryListData.isFetchingNextPage) {
      categoryListData.fetchNextPage()
    }
  }

  useEffect(() => {
    if (!sortBy || (!category && !category_id)) {
      handleSortClick({ value: DEFAULT_MALL_SORT, queryParams: { category: (category as string) || '-1' }, hasOrder: false })
    }
  }, [category, category_id, sortBy])

  const handleSortClick = ({ value, queryParams, hasOrder }: { value: string; queryParams?: { category: string }; hasOrder: boolean }) => {
    let newOrder = ''

    if (sortBy === value) {
      if (hasOrder) {
        newOrder = order === ORDER.DESCEND ? ORDER.ASCEND : ORDER.DESCEND
      } else {
        newOrder = DEFAULT_MALL_ORDER
      }
    } else {
      newOrder = DEFAULT_MALL_ORDER
    }

    handleRoutePushWithQuery({
      router,
      pathname,
      query: { ...query, ...queryParams, sortBy: value, order: newOrder },
      isReplace: true,
      scroll: false,
    })
  }

  return (
    <div className={cx('mallList', className)}>
      {/* TODO: 目前只有 mall 首頁的商品列表 api 有 sort 功能，等 user 跟 search 的 api 有支援 sort 再全開 */}
      {isMallPage && (
        <div className={cx('sort-wrapper')}>
          {category_id && <p className={cx('sort-wrapper-text')}>{t('Mall_List_Title')}</p>}
          <div className={cx('sort-wrapper-list')}>
            <SortItem
              label={t('Mall_Sort_Recommend')}
              value={MALL_SORT.RECOMMEND}
              order=''
              handleSortClick={handleSortClick}
              isActive={sortBy === MALL_SORT.RECOMMEND}
              hasOrder={false}
            />
            {/* TODO: 目前後端沒實作推薦排序，如果沒給 sort 就會跟時間排序一樣，所以先把最新商品的排序拿掉，等後端有空處理推薦排序再調整 */}
            {/* <SortItem
            label={t('Mall_Sort_New')}
            value={MALL_SORT.DATE}
            order={order as string}
            handleSortClick={handleSortClick}
            isActive={sortBy === MALL_SORT.DATE}
            hasOrder={false}
          /> */}
            <SortItem
              label={t('Mall_Sort_Sale')}
              value={MALL_SORT.STOCK}
              order={order as string}
              handleSortClick={handleSortClick}
              isActive={sortBy === MALL_SORT.STOCK}
            />
            <SortItem
              label={t('Mall_Sort_Price')}
              value={MALL_SORT.PRICE}
              order={order as string}
              handleSortClick={handleSortClick}
              isActive={sortBy === MALL_SORT.PRICE}
            />
          </div>
        </div>
      )}

      {categoryListData?.isSuccess && !categoryListData?.data?.pages[0]?.productsInfo && <Empty />}

      {/* Category List */}
      {isVirtualized ? (
        <>
          {categoryListData?.isSuccess && categoryListData?.data?.pages.length > 0 && categoryListData?.data?.pages[0]?.productsInfo && (
            <VirtualizedMallList observer={observerRef.current} categoryListData={categoryListData} gtmItemEventParams={gtmItemEventParams} />
          )}
        </>
      ) : (
        <InfiniteScroll className={cx('mallList__list')} loadMore={handleNextpage} hasMore={categoryListData.hasNextPage}>
          {!categoryListData?.isSuccess && (
            <ProductCardSkeleton count={isMobile ? 6 : categoryListData?.data?.pages.length * MALL_DEFAULT_PAGE_SIZE || 20} />
          )}
          {categoryListData?.isSuccess &&
            categoryListData?.data?.pages.length > 0 &&
            categoryListData?.data?.pages.map((item: any, pageIndex: number) => (
              <React.Fragment key={pageIndex}>
                {typeof window !== undefined &&
                  item?.productsInfo?.map((item: any) => (
                    <ProductCard key={item.productId} {...item} observer={observerRef.current} gtmItemEventParams={gtmItemEventParams} />
                  ))}
              </React.Fragment>
            ))}
          {categoryListData?.isFetchingNextPage && <ProductCardSkeleton count={14} />}
        </InfiniteScroll>
      )}

      {/* Category List */}

      {categoryListData?.isSuccess && !categoryListData?.data?.pages[0]?.productsInfo && <HotLiveList />}
    </div>
  )
}

export default MallList
